import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Session } from '@supabase/supabase-js';
import { config } from '../lib/config';
import {
  LOCATION_COPY_DEFAULTS,
  LOCATION_COPY_FIELDS,
  LOCATION_COPY_PREVIEW_SAMPLE_VARS,
  LOCATION_COPY_SPORT_EDITOR_OPTIONS,
  LOCATION_COPY_STYLE_EDITOR_OPTIONS,
  LOCATION_COPY_VARIANT_EDITOR_OPTIONS,
  cloneValue,
  countOverridesForFields,
  deletePathValue,
  fieldsForSection,
  getContextualPathValue,
  getEffectiveLocationComposerCopy,
  getPathValue,
  normalizeLocationComposerOverrides,
  resolveScopedFieldPath,
  sectionLabel,
  setPathValue,
  textValue,
  type LocationCopyEditorScope,
  type LocationCopyField,
  type LocationCopyPreviewContext,
} from './schema';

type StatusTone = 'info' | 'success' | 'error' | 'warning';
type SaveState = 'saved' | 'saving' | 'unsaved' | 'error';
type PreviewViewport = 'phone' | 'tablet' | 'desktop';
type PreviewTarget = 'base' | 'style' | 'sport' | 'variant';
type PreviewScenario = 'selected' | 'unselected' | 'empty';

type StatusMessage = {
  tone: StatusTone;
  text: string;
};

type AdminConsoleResponse<T> = { ok?: boolean; error?: string; value?: unknown; settings?: unknown } & T;

type DraftMeta = {
  updated_at: string | null;
  updated_by: string | null;
};

type LocationCopyGetResponse = {
  value?: Record<string, unknown>;
  settings?: { updated_at?: string | null; updated_by?: string | null } | null;
};

const LOCATION_COPY_KEY = 'location_composer_copy_v1';
const DEFAULT_EDITOR_SCOPE: LocationCopyEditorScope = { kind: 'base' };
const DEFAULT_PREVIEW_CONTEXT: LocationCopyPreviewContext = {
  copyStyle: 'game',
  sportId: 'soccer',
  variant: null,
  activityLabel: 'Soccer',
};

function scopeSummary(scope: LocationCopyEditorScope) {
  switch (scope.kind) {
    case 'style':
      return `Family override · ${scope.copyStyle}`;
    case 'sport':
      return `Sport override · ${scope.sportId}`;
    case 'variant':
      return `Variant override · ${scope.sportId} / ${scope.variant}`;
    case 'base':
    default:
      return 'Base copy';
  }
}

async function adminFetch<T>(
  session: Session,
  payload: Record<string, unknown>
): Promise<AdminConsoleResponse<T>> {
  const url = `${config.supabaseUrl}/functions/v1/admin-console`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: config.supabaseAnonKey,
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await response.json().catch(() => ({}))) as AdminConsoleResponse<T>;
  if (!response.ok || body?.error) {
    throw new Error(body?.error || `Request failed (${response.status})`);
  }
  return body;
}

function sectionDescription(section: LocationCopyField['section']) {
  switch (section) {
    case 'intro':
      return 'Top-of-sheet context and planning language.';
    case 'search':
      return 'Search input guidance.';
    case 'selection':
      return 'Selected place and primary CTA language.';
    case 'browse':
      return 'Lower tabs and browse labels.';
    case 'empty':
      return 'Fallback copy when the map has nothing to show.';
    default:
      return '';
  }
}

export default function LocationCopyWorkspace({ session }: { session: Session }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [meta, setMeta] = useState<DraftMeta>({ updated_at: null, updated_by: null });
  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [editorScope, setEditorScope] = useState<LocationCopyEditorScope>(DEFAULT_EDITOR_SCOPE);
  const [selectedFieldPath, setSelectedFieldPath] = useState<string>(LOCATION_COPY_FIELDS[0]?.path ?? '');
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>('phone');
  const [previewScenario, setPreviewScenario] = useState<PreviewScenario>('selected');
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget>('sport');
  const [previewContext, setPreviewContext] = useState<LocationCopyPreviewContext>(DEFAULT_PREVIEW_CONTEXT);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);

  const previewOrigin = useMemo(() => {
    if (typeof window === 'undefined') return 'https://play.rvlryapp.com';
    const params = new URLSearchParams(window.location.search);
    const overridden = params.get('playPreviewOrigin');
    if (overridden) return overridden.replace(/\/$/, '');
    return 'https://play.rvlryapp.com';
  }, []);

  const previewUrl = useMemo(() => {
    const query = new URLSearchParams({
      sportId: previewContext.sportId ?? 'pickleball',
      variant: previewContext.variant ?? '',
      viewport: previewViewport,
      scenario: previewScenario,
    });
    return `${previewOrigin}/preview/location-composer?${query.toString()}`;
  }, [previewContext.sportId, previewContext.variant, previewOrigin, previewScenario, previewViewport]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const result = await adminFetch<LocationCopyGetResponse>(session, { action: 'location_composer_copy:get' });
        if (cancelled) return;
        const value =
          result.value && typeof result.value === 'object' && !Array.isArray(result.value)
            ? (result.value as Record<string, unknown>)
            : {};
        setBaseline(value);
        setDraft(cloneValue(value));
        setMeta({
          updated_at: result.settings?.updated_at ?? null,
          updated_by: result.settings?.updated_by ?? null,
        });
        setStatus(null);
        setSaveState('saved');
      } catch (error) {
        if (cancelled) return;
        setStatus({ tone: 'error', text: (error as Error).message || 'Failed to load location composer copy.' });
        setSaveState('error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const isDirty = useMemo(
    () => JSON.stringify(normalizeLocationComposerOverrides(draft)) !== JSON.stringify(normalizeLocationComposerOverrides(baseline)),
    [baseline, draft]
  );

  useEffect(() => {
    setSaveState((current) => {
      if (current === 'saving') return current;
      return isDirty ? 'unsaved' : 'saved';
    });
  }, [isDirty]);

  const effectivePreview = useMemo(
    () =>
      getEffectiveLocationComposerCopy(
        draft,
        previewTarget === 'base'
          ? null
          : previewTarget === 'style'
            ? { copyStyle: previewContext.copyStyle }
            : previewTarget === 'sport'
              ? { copyStyle: previewContext.copyStyle, sportId: previewContext.sportId }
              : previewContext
      ),
    [draft, previewContext, previewTarget]
  );

  const selectedField = useMemo(
    () => LOCATION_COPY_FIELDS.find((field) => field.path === selectedFieldPath) ?? LOCATION_COPY_FIELDS[0] ?? null,
    [selectedFieldPath]
  );

  const selectedScopedPath = useMemo(
    () => (selectedField ? resolveScopedFieldPath(selectedField.path, editorScope) : ''),
    [editorScope, selectedField]
  );

  const selectedValue = useMemo(
    () => textValue(getPathValue(draft, selectedScopedPath)),
    [draft, selectedScopedPath]
  );

  const selectedDefaultValue = useMemo(
    () =>
      selectedField
        ? textValue(
            getContextualPathValue(
              getEffectiveLocationComposerCopy({}, previewTarget === 'base' ? null : previewContext),
              selectedField.path,
              previewTarget === 'base' ? null : previewContext
            )
          )
        : '',
    [previewContext, previewTarget, selectedField]
  );

  const pushPreviewState = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    target.postMessage(
      {
        source: 'rvlry-admin-location-copy',
        type: 'location-copy-preview:update',
        payload: {
          copySettings: effectivePreview,
          sportId: previewContext.sportId,
          variant: previewContext.variant,
          viewport: previewViewport,
          scenario: previewScenario,
        },
      },
      previewOrigin
    );
  }, [effectivePreview, previewContext.sportId, previewContext.variant, previewOrigin, previewScenario, previewViewport]);

  useEffect(() => {
    if (!iframeLoaded) return;
    const timer = window.setTimeout(() => pushPreviewState(), 80);
    return () => window.clearTimeout(timer);
  }, [iframeLoaded, pushPreviewState]);

  const updateField = useCallback(
    (field: LocationCopyField, nextValue: string) => {
      setDraft((current) => {
        const next = cloneValue(current);
        const scopedPath = resolveScopedFieldPath(field.path, editorScope);
        const defaultValue = textValue(getPathValue(LOCATION_COPY_DEFAULTS, field.path));
        if (!nextValue.trim().length || (editorScope.kind === 'base' && nextValue === defaultValue)) {
          deletePathValue(next, scopedPath);
        } else {
          setPathValue(next, scopedPath, nextValue);
        }
        return next;
      });
    },
    [editorScope]
  );

  const saveDraft = useCallback(async () => {
    try {
      setSaveState('saving');
      setStatus({ tone: 'info', text: 'Saving location composer copy…' });
      const value = normalizeLocationComposerOverrides(draft);
      const result = await adminFetch<LocationCopyGetResponse>(session, {
        action: 'location_composer_copy:save',
        value,
        expectedUpdatedAt: meta.updated_at,
      });
      const nextValue =
        result.value && typeof result.value === 'object' && !Array.isArray(result.value)
          ? (result.value as Record<string, unknown>)
          : {};
      setBaseline(nextValue);
      setDraft(cloneValue(nextValue));
      setMeta({
        updated_at: result.settings?.updated_at ?? null,
        updated_by: result.settings?.updated_by ?? null,
      });
      setSaveState('saved');
      setStatus({ tone: 'success', text: 'Location composer copy saved.' });
    } catch (error) {
      setSaveState('error');
      setStatus({ tone: 'error', text: (error as Error).message || 'Failed to save location composer copy.' });
    }
  }, [draft, meta.updated_at, session]);

  const sectionGroups = useMemo(
    () =>
      (['intro', 'search', 'selection', 'browse', 'empty'] as const).map((section) => ({
        section,
        label: sectionLabel(section),
        description: sectionDescription(section),
        fields: fieldsForSection(section),
      })),
    []
  );

  const editorVariantOptions = useMemo(
    () => (editorScope.kind === 'variant' ? LOCATION_COPY_VARIANT_EDITOR_OPTIONS[editorScope.sportId] ?? [] : []),
    [editorScope]
  );

  const previewVariantOptions = useMemo(
    () => (previewContext.sportId ? LOCATION_COPY_VARIANT_EDITOR_OPTIONS[previewContext.sportId] ?? [] : []),
    [previewContext.sportId]
  );

  if (loading) {
    return <div className="admin-page cms-workspace match-copy-workspace"><div className="admin-card">Loading…</div></div>;
  }

  return (
    <div className="admin-page cms-workspace match-copy-workspace">
      <div className="admin-page-head">
        <div>
          <div className="admin-h1">Location composer copy</div>
          <div className="admin-subtle">Stored in <span className="admin-mono">app_settings.{LOCATION_COPY_KEY}</span>.</div>
        </div>
        <div className="admin-actions">
          <span className={clsx('cms-save-pill', `is-${saveState}`)}>{saveState}</span>
          <button className="admin-btn primary" type="button" onClick={() => void saveDraft()} disabled={saveState === 'saving' || !isDirty}>
            Save
          </button>
        </div>
      </div>

      {status?.text ? <div className={clsx('admin-status', status.tone)}>{status.text}</div> : null}

      <div className="cms-layout">
        <aside className="cms-editor-pane match-copy-editor-pane">
          <div className="cms-page-context cms-soft-card">
            <div className="cms-page-context-head">
              <div>
                <div className="cms-editor-form-kicker">Current surface</div>
                <h3 className="cms-page-context-title">Location Composer Copy</h3>
              </div>
              <div className="cms-page-context-route">{config.hubActiveEnv}</div>
            </div>
            <div className="cms-page-context-meta">
              <span className="admin-badge">{countOverridesForFields(draft, LOCATION_COPY_FIELDS, editorScope)} overrides</span>
              <span className="admin-muted">Updated {meta.updated_at ? new Date(meta.updated_at).toLocaleString() : 'never'}</span>
            </div>

            <div className="cms-editor-form-section" style={{ marginTop: 12 }}>
              <div className="cms-editor-form-section-head">
                <div className="cms-editor-form-section-kicker">Editing scope</div>
                <h4>{scopeSummary(editorScope)}</h4>
              </div>
              <div className="cms-page-context-actions" style={{ marginBottom: 10 }}>
                <button className={clsx('admin-btn small', editorScope.kind === 'base' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'base' })}>Base</button>
                <button className={clsx('admin-btn small', editorScope.kind === 'style' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'style', copyStyle: previewContext.copyStyle })}>Family</button>
                <button className={clsx('admin-btn small', editorScope.kind === 'sport' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'sport', sportId: previewContext.sportId ?? 'pickleball' })}>Sport</button>
                <button className={clsx('admin-btn small', editorScope.kind === 'variant' && 'primary')} type="button" onClick={() => setEditorScope({ kind: 'variant', sportId: previewContext.sportId ?? 'weightlifting', variant: previewContext.variant ?? 'one_rep_max' })}>Variant</button>
              </div>

              <div className="match-copy-settings-grid">
                {editorScope.kind === 'style' ? (
                  <label className="admin-label">
                    <span>Family</span>
                    <select className="admin-input" value={editorScope.copyStyle} onChange={(event) => setEditorScope({ kind: 'style', copyStyle: event.target.value as LocationCopyPreviewContext['copyStyle'] })}>
                      {LOCATION_COPY_STYLE_EDITOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {editorScope.kind === 'sport' || editorScope.kind === 'variant' ? (
                  <label className="admin-label">
                    <span>Sport</span>
                    <select
                      className="admin-input"
                      value={editorScope.sportId}
                      onChange={(event) => {
                        const sportId = event.target.value;
                        if (editorScope.kind === 'sport') {
                          setEditorScope({ kind: 'sport', sportId });
                          return;
                        }
                        const variants = LOCATION_COPY_VARIANT_EDITOR_OPTIONS[sportId] ?? [];
                        setEditorScope({ kind: 'variant', sportId, variant: variants[0]?.value ?? 'one_rep_max' });
                      }}
                    >
                      {LOCATION_COPY_SPORT_EDITOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {editorScope.kind === 'variant' ? (
                  <label className="admin-label">
                    <span>Variant</span>
                    <select className="admin-input" value={editorScope.variant} onChange={(event) => setEditorScope({ kind: 'variant', sportId: editorScope.sportId, variant: event.target.value })}>
                      {editorVariantOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            </div>
          </div>

          <div className="cms-editor-stack match-copy-editor-stack">
            <div className="cms-card-stack cms-soft-card cms-section-column match-copy-section-column">
              <div className="cms-pane-head">
                <div>
                  <div className="cms-editor-form-kicker">Fields</div>
                  <h3 className="cms-page-context-title">Composer surface</h3>
                </div>
              </div>

              <div className="match-copy-field-groups">
                {sectionGroups.map((group) => (
                  <div className="match-copy-field-group" key={group.section}>
                    <div className="match-copy-field-group-head">
                      <div>
                        <strong>{group.label}</strong>
                        <span>{group.description}</span>
                      </div>
                      <span>{countOverridesForFields(draft, group.fields, editorScope)} overrides</span>
                    </div>
                    <div className="match-copy-field-list">
                      {group.fields.map((field) => (
                        <button
                          key={field.path}
                          className={clsx('match-copy-field-list-item', selectedField?.path === field.path && 'active')}
                          type="button"
                          onClick={() => setSelectedFieldPath(field.path)}
                        >
                          <strong>{field.label}</strong>
                          <span>{textValue(getPathValue(draft, resolveScopedFieldPath(field.path, editorScope))) || textValue(getPathValue(LOCATION_COPY_DEFAULTS, field.path))}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cms-soft-card cms-card-stack cms-editor-column match-copy-detail-column">
              {selectedField ? (
                <>
                  <div className="cms-editor-form-head">
                    <div>
                      <div className="cms-editor-form-kicker">{selectedField.section}</div>
                      <h3 className="cms-editor-form-title">{selectedField.label}</h3>
                    </div>
                    <div className="match-copy-detail-meta">
                      <span className="admin-mono">{selectedScopedPath}</span>
                    </div>
                  </div>

                  <div className="cms-editor-form-section">
                    <div className="cms-editor-form-section-head">
                      <div className="cms-editor-form-section-kicker">Core content</div>
                    </div>
                    <textarea
                      className="admin-input match-copy-body-input"
                      rows={selectedField.path === 'emptyState.body' || selectedField.path === 'intro.subtitle' ? 4 : 2}
                      spellCheck={false}
                      value={selectedValue}
                      onChange={(event) => updateField(selectedField, event.target.value)}
                    />
                    <div className="cms-page-context-actions">
                      <button className="admin-btn small" type="button" onClick={() => updateField(selectedField, '')}>Reset field</button>
                    </div>
                  </div>

                  <div className="cms-editor-form-section">
                    <div className="cms-editor-form-section-head">
                      <div className="cms-editor-form-section-kicker">Comparison</div>
                    </div>
                    <div className="match-copy-compare-grid">
                      <div className="match-copy-compare-card">
                        <div className="match-copy-compare-title">Inherited default</div>
                        <div className="match-copy-compare-body">{selectedDefaultValue || '—'}</div>
                      </div>
                      <div className="match-copy-compare-card accent">
                        <div className="match-copy-compare-title">Effective output</div>
                        <div className="match-copy-compare-body">
                          {textValue(
                            getContextualPathValue(effectivePreview, selectedField.path, previewTarget === 'base' ? null : previewContext)
                          ) || '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="cms-soft-card cms-empty-editor">Select a field to edit.</div>
              )}
            </div>
          </div>
        </aside>

        <section className="cms-preview-pane">
          <div className="cms-preview-pane-head">
            <div>
              <div className="admin-section-title">Preview canvas</div>
              <div className="cms-preview-status">
                {iframeError ? 'Preview failed to load. Retry or open it in a new tab.' : iframeLoaded ? 'Contextual composer preview' : 'Loading real play preview…'}
              </div>
            </div>
            <div className="cms-stage-header-actions">
              <button className={clsx('admin-btn small', previewViewport === 'phone' && 'primary')} type="button" onClick={() => setPreviewViewport('phone')}>Phone</button>
              <button className={clsx('admin-btn small', previewViewport === 'tablet' && 'primary')} type="button" onClick={() => setPreviewViewport('tablet')}>Tablet</button>
              <button className={clsx('admin-btn small', previewViewport === 'desktop' && 'primary')} type="button" onClick={() => setPreviewViewport('desktop')}>Desktop</button>
              <button className="admin-btn small" type="button" onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>Open in new tab</button>
            </div>
          </div>

          <div className="cms-soft-card cms-card-stack cms-editor-details">
            <div className="cms-pane-head">
              <div>
                <div className="cms-editor-form-kicker">Context</div>
                <h3 className="cms-page-context-title">Preview settings</h3>
              </div>
            </div>

            <div className="cms-editor-form-section">
              <div className="cms-page-context-actions">
                <button className={clsx('admin-btn small', previewTarget === 'base' && 'primary')} type="button" onClick={() => setPreviewTarget('base')}>Base</button>
                <button className={clsx('admin-btn small', previewTarget === 'style' && 'primary')} type="button" onClick={() => setPreviewTarget('style')}>Family</button>
                <button className={clsx('admin-btn small', previewTarget === 'sport' && 'primary')} type="button" onClick={() => setPreviewTarget('sport')}>Sport</button>
                <button className={clsx('admin-btn small', previewTarget === 'variant' && 'primary')} type="button" onClick={() => setPreviewTarget('variant')}>Variant</button>
              </div>

              <div className="match-copy-settings-grid">
                <label className="admin-label">
                  <span>Family</span>
                  <select
                    className="admin-input"
                    value={previewContext.copyStyle}
                    onChange={(event) => setPreviewContext((current) => ({ ...current, copyStyle: event.target.value as LocationCopyPreviewContext['copyStyle'] }))}
                  >
                    {LOCATION_COPY_STYLE_EDITOR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-label">
                  <span>Sport</span>
                  <select
                    className="admin-input"
                    value={previewContext.sportId ?? ''}
                    onChange={(event) => {
                      const sportId = event.target.value;
                      const variants = LOCATION_COPY_VARIANT_EDITOR_OPTIONS[sportId] ?? [];
                      setPreviewContext((current) => ({
                        ...current,
                        sportId,
                        variant: previewTarget === 'variant' ? variants[0]?.value ?? null : current.variant,
                      }));
                    }}
                  >
                    {LOCATION_COPY_SPORT_EDITOR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-label">
                  <span>Variant</span>
                  <select
                    className="admin-input"
                    value={previewContext.variant ?? ''}
                    onChange={(event) => setPreviewContext((current) => ({ ...current, variant: event.target.value || null }))}
                  >
                    <option value="">None</option>
                    {previewVariantOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-label">
                  <span>Scenario</span>
                  <select className="admin-input" value={previewScenario} onChange={(event) => setPreviewScenario(event.target.value as PreviewScenario)}>
                    <option value="selected">Selected location</option>
                    <option value="unselected">Unselected location</option>
                    <option value="empty">Empty state</option>
                  </select>
                </label>
              </div>

              <div className="admin-muted">
                Tokens: {'{activityLabel}'}, {'{activityQuestionPhrase}'}, {'{activityPlanPhrase}'}, {'{activityPrimaryCta}'}.
                Sample: {LOCATION_COPY_PREVIEW_SAMPLE_VARS.activityLabel} / {LOCATION_COPY_PREVIEW_SAMPLE_VARS.activityQuestionPhrase} / {LOCATION_COPY_PREVIEW_SAMPLE_VARS.activityPrimaryCta}
              </div>
            </div>
          </div>

          <div className="cms-stage">
            <div className="cms-stage-canvas">
              <div className={clsx('match-copy-preview-frame', 'iframe-shell', previewViewport)}>
                {!iframeLoaded ? <div className="cms-preview-overlay">Loading preview…</div> : null}
                {iframeError ? <div className="cms-preview-overlay error">{iframeError}</div> : null}
                <iframe
                  ref={iframeRef}
                  title="Location composer preview"
                  src={previewUrl}
                  className="cms-match-preview-iframe"
                  onLoad={() => {
                    setIframeLoaded(true);
                    setIframeError(null);
                    window.setTimeout(() => pushPreviewState(), 40);
                  }}
                  onError={() => {
                    setIframeLoaded(false);
                    setIframeError('Unable to load the play preview route.');
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
